import type { AccessCategory, ClassificationBody, Marathon } from '@/api/courses';

/**
 * Подтип и категория доступа курса — разбор и сборка запроса (договор
 * `docs/CONTRACTS/mirror-v2.md` §2).
 *
 * ⚠️ ПОДТИП ВТЯГИВАЕТ КУРС В ЗЕРКАЛО. Его содержимое разъезжается по живым
 * курсам потока, а чужое приезжает к нему — и на черновик это правило тоже
 * распространяется. Поэтому здесь нет ни одного «удобного» умолчания: всё, что
 * может молча снять подтип, сделано невозможным по устройству.
 *
 * ⚠️ ОТСУТСТВИЕ КЛЮЧА ≠ `null`. Сервер смотрит, БЫЛ ЛИ КЛЮЧ в теле: нет ключа —
 * поле не трогается, `null` — снимается. Значит нельзя собирать тело из полного
 * набора полей: сохранение одной категории доступа послало бы `subtype: null` и
 * вывело курс из потока — заметил бы это не админ, а ученики, у которых
 * перестало приезжать содержимое.
 */

/** Вид подтипа — зеркало серверного `subtypePattern` (service/marathon_classification.go). */
const SUBTYPE_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

/** Предел из колонки `varchar(64)`. */
export const SUBTYPE_MAX = 64;

export const ACCESS_CATEGORIES: readonly AccessCategory[] = ['self', 'one_to_one', 'one_to_group'];

/**
 * Подтип из поля ввода: пробелы по краям срезаем, пустая строка означает «снять».
 *
 * Регистр и прочее НЕ правим: подтип `SEPT26` — это ошибка, и человек должен её
 * увидеть, а не получить молча исправленное значение, которое он не набирал.
 */
export function normalizeSubtypeInput(raw: string): string {
  return raw.trim();
}

/** Годится ли подтип. Пустая строка — не подтип, а его снятие (см. `classificationBody`). */
export function isValidSubtype(value: string): boolean {
  return value.length > 0 && value.length <= SUBTYPE_MAX && SUBTYPE_PATTERN.test(value);
}

export type ClassificationDraft = {
  /** Что в поле подтипа сейчас; пустая строка — «снять». */
  subtype: string;
  category: AccessCategory | null;
};

export type ClassificationSaved = {
  /** Как на сервере: пустая строка — подтипа нет. */
  subtype: string;
  category: AccessCategory | null;
};

/** Меняется ли подтип. Отдельная проверка: от неё зависит окно подтверждения. */
export function subtypeChanges(saved: ClassificationSaved, draft: ClassificationDraft): boolean {
  return normalizeSubtypeInput(draft.subtype) !== saved.subtype;
}

export function categoryChanges(saved: ClassificationSaved, draft: ClassificationDraft): boolean {
  return draft.category !== saved.category;
}

/**
 * Тело запроса — или `null`, когда посылать нечего.
 *
 * `canEditSubtype` — признак `subtype_editable` с сервера. Без права подтип в
 * тело не попадает НИКОГДА, даже если черновик отличается: иначе сохранение
 * категории у обычного админа получало бы 403 и не проходило вовсе.
 */
export function classificationBody(
  saved: ClassificationSaved,
  draft: ClassificationDraft,
  title: string,
  canEditSubtype: boolean,
): ClassificationBody | null {
  const body: ClassificationBody = {};

  if (canEditSubtype && subtypeChanges(saved, draft)) {
    const next = normalizeSubtypeInput(draft.subtype);
    body.subtype = next === '' ? null : next;
    // Подтверждение идёт вместе с подтипом и только с ним: с одной категорией
    // сервер ответил бы `nothing_to_change`, а не принял бы лишнее поле.
    body.confirm_title = title;
  }

  if (categoryChanges(saved, draft)) {
    body.access_category = draft.category;
  }

  return Object.keys(body).length ? body : null;
}

/**
 * Подтипы, которые уже есть у живых курсов, — подсказка для поля ввода.
 *
 * Образцы (`is_template`) пропускаем: курсами подтипа они не считаются (договор
 * §2, «сторож входа»), и предлагать их подтип значит звать в поток, которого
 * нет.
 */
export function knownSubtypes(marathons: readonly Pick<Marathon, 'subtype' | 'is_template'>[]): string[] {
  const seen = new Set<string>();
  for (const m of marathons) {
    if (m.is_template) continue;
    const s = (m.subtype ?? '').trim();
    if (s) seen.add(s);
  }
  return [...seen].sort();
}
